import React, { useState, useRef, useEffect } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { Button, Container } from 'react-bootstrap';

import BayFilesUpload from '../../BayFilesUpload';
import BayModal from '../../BayModal';
import BayRangeSlider from '../../BayRangeSlider';
import BayTooltip from '../../BayTooltip';
import BayWidgetLoadingPlaceholder from '../../BayWidgetLoadingPlaceholder';
import { Facebook, Instagram, Linkedin, Youtube } from '../../Icons';

interface FileData {
	_id?: string;
	thumbnailUrls?: {
		custom?: string;
		default?: string;
	};
	[key: string]: unknown;
}

interface ShowModalState {
	show: boolean;
	rowData?: FileData;
}

interface EditThumbnailModalProps {
	t: (key: string) => string;
	showModal: ShowModalState;
	setShowModal: (state: ShowModalState) => void;
	file: FileData;
	onSave: (thumbnailFile: File, rowData: FileData | undefined, isCustom: boolean) => Promise<void>;
	onRequestDelete: (fileName: string, rowData: FileData | undefined) => void;
	isLoading?: boolean;
}

const EditThumbnailModal: React.FC<EditThumbnailModalProps> = ({
	t,
	showModal,
	setShowModal,
	file,
	onSave,
	onRequestDelete,
	isLoading = false,
}) => {
	const editorRef = useRef<AvatarEditor>(null);
	const [scale, setScale] = useState(1);
	const [showGrid, setShowGrid] = useState(false);
	const [image] = useState(file?.thumbnailUrls?.custom || null);
	const [tempImage, setTempImage] = useState<string | null>(null);
	const [fileType, setFileType] = useState('image/jpeg');
	const [initialScale, setInitialScale] = useState(1);
	const [hasChanges, setHasChanges] = useState(false);

	const [showChangeImageFileError, setShowChangeImageFileError] =
		useState(false);
	const [changeImageFileErrorMsg, setChangeImageFileErrorMsg] = useState('');

	const hasCustomThumbnail = !!file?.thumbnailUrls?.custom;

	// Effect to track changes to the thumbnail
	useEffect(() => {
		// If there's a tempImage, changes have been made
		if (tempImage) {
			setHasChanges(true);
			return;
		}

		// If scale has changed from initial value, changes have been made
		if (scale !== initialScale) {
			setHasChanges(true);
			return;
		}

		// No changes detected
		setHasChanges(false);
	}, [tempImage, scale, initialScale]);

	const handleCancel = () => {
		setShowModal({ show: false });
		// Clean up any temp images
		if (tempImage) {
			URL.revokeObjectURL(tempImage);
		}
		setTempImage(null);
		setFileType('image/jpeg');
		setScale(1);
		setInitialScale(1);
		setShowGrid(false);
		setHasChanges(false);
	};

	const handleSave = async () => {
		if (editorRef.current) {
			try {
				const canvas = editorRef.current.getImageScaledToCanvas();
				// Use the detected file type to preserve the format
				const dataUrl = canvas.toDataURL(fileType);

				// Convert dataUrl to Blob for upload
				const res = await fetch(dataUrl);
				const blob = await res.blob();

				// Create extension based on mime type
				const extension = fileType === 'image/png' ? 'png' : 'jpg';
				const thumbnailFile = new File(
					[blob],
					`${file._id}_custom_thumbnail.${extension}`,
					{ type: fileType },
				);

				await onSave(thumbnailFile, showModal.rowData, true);

				setShowModal({ show: false });
				setTempImage(null);
			} catch (error) {
				console.error('Error saving thumbnail:', error);
			}
		}
	};

	const handleConfirmDelete = () => {
		// Show the delete confirmation modal by calling the parent handler
		setShowModal({ show: false });
		const fileName = file?.thumbnailUrls?.custom?.substring(
			file?.thumbnailUrls?.custom.lastIndexOf('/') + 1,
			file?.thumbnailUrls?.custom.lastIndexOf('?'),
		);
		if (fileName) {
			onRequestDelete(fileName, showModal.rowData);
		}
	};

	const handleChangeImage = () => {
		if (tempImage) {
			setTempImage(null);
			setFileType('image/jpeg');
			setScale(initialScale);
			setHasChanges(false);
		} else {
			const fileInput = document.getElementById('thumbnailFile') as HTMLInputElement;
			if (fileInput) {
				fileInput.click();
			}
		}
	};

	const handleChangeImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Validate that the file is an image file
		const isValidImageType =
			file.type === 'image/jpeg' ||
			file.type === 'image/jpg' ||
			file.type === 'image/png';

		if (!isValidImageType) {
			// Show error for invalid file type
			setChangeImageFileErrorMsg(
				t('invalidFileTypeError') ||
					'Only JPG, JPEG, and PNG images are allowed.',
			);
			setShowChangeImageFileError(true);
			return;
		}

		// Check file size (5MB max)
		if (file.size > 5242880) {
			// Show error for file too large
			setChangeImageFileErrorMsg(
				t('fileTooLargeError') || 'Maximum image file size is 5MB.',
			);
			setShowChangeImageFileError(true);
			return;
		}

		setHasChanges(true);

		// Detect and set the file type
		if (file.type === 'image/png') {
			setFileType('image/png');
		} else {
			setFileType('image/jpeg');
		}

		const reader = new FileReader();
		reader.onload = () => {
			setTempImage(reader.result as string);
		};
		reader.readAsDataURL(file);
	};

	const handleChangeImageFileError = () => {
		setChangeImageFileErrorMsg(
			t('changeImageFileError') ||
				'There was an error processing your image file.',
		);
		setShowChangeImageFileError(true);
	};

	const handleDrop = (acceptedFiles: File[]) => {
		if (acceptedFiles && acceptedFiles.length > 0) {
			const file = acceptedFiles[0];

			// Validate that the file is an image file
			const isValidImageType =
				file.type === 'image/jpeg' ||
				file.type === 'image/jpg' ||
				file.type === 'image/png';

			if (!isValidImageType) {
				// Show error for invalid file type
				setChangeImageFileErrorMsg(
					t('invalidFileTypeError') ||
						'Only JPG, JPEG, and PNG images are allowed.',
				);
				setShowChangeImageFileError(true);
				return;
			}

			// Check file size (5MB max)
			if (file.size > 5242880) {
				// Show error for file too large
				setChangeImageFileErrorMsg(
					t('fileTooLargeError') || 'Maximum image file size is 5MB.',
				);
				setShowChangeImageFileError(true);
				return;
			}

			setHasChanges(true);

			// Detect and set the file type
			if (file.type === 'image/png') {
				setFileType('image/png');
			} else {
				setFileType('image/jpeg');
			}

			const reader = new FileReader();
			reader.onload = () => {
				setTempImage(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	// Add event listener to prevent default for file drops outside of BayFilesUpload
	useEffect(() => {
		// This prevents the browser from handling the drop event (which would navigate away from the page)
		const handleDragOver = (e: DragEvent) => {
			e.preventDefault();
		};

		// Show error when files are dropped outside of the dropzone
		const handleDropEvent = (e: DragEvent) => {
			e.preventDefault();

			if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
				const droppedFile = e.dataTransfer.files[0];
				const isValidImageType =
					droppedFile.type === 'image/jpeg' ||
					droppedFile.type === 'image/jpg' ||
					droppedFile.type === 'image/png';

				if (!isValidImageType) {
					setChangeImageFileErrorMsg(
						t('changeImageFileError') ||
							'Only JPG, JPEG, and PNG images are allowed.',
					);
					setShowChangeImageFileError(true);
				}
			}
		};

		// Add the event listeners
		document.addEventListener('dragover', handleDragOver);
		document.addEventListener('drop', handleDropEvent);

		// Clean up
		return () => {
			document.removeEventListener('dragover', handleDragOver);
			document.removeEventListener('drop', handleDropEvent);
		};
	}, [t]);

	const renderModalFooter = () => {
		return (
			<div className="d-flex justify-content-between w-100">
				<Button
					disabled={isLoading}
					variant="outline-secondary"
					onClick={handleCancel}
				>
					{t('cancel')}
				</Button>
				<div className="d-flex">
					{(hasCustomThumbnail || tempImage) && (
						<>
							<BayTooltip
								disabled={
									isLoading || !tempImage || (hasCustomThumbnail && !tempImage)
								}
								overlay="There are unsaved changes. Please save the changes before deleting the thumbnail."
								placement="top"
							>
								<div
									className={`tooltip-container ${isLoading || !hasCustomThumbnail || tempImage ? 'disabled cursor-not-allowed' : ''}`}
								>
									<Button
										disabled={isLoading || !hasCustomThumbnail || !!tempImage}
										variant="outline-danger me-3"
										onClick={handleConfirmDelete}
									>
										{t('deleteThumbnail')}
									</Button>
								</div>
							</BayTooltip>
							<Button
								disabled={isLoading}
								variant="outline-primary me-3"
								onClick={handleChangeImage}
							>
								{t('changeImage')}
							</Button>
						</>
					)}
					<Button
						disabled={
							(!tempImage && !image) ||
							isLoading ||
							(hasCustomThumbnail && !hasChanges)
						}
						variant="primary"
						onClick={handleSave}
					>
						{t('save')}
					</Button>
				</div>
				{hasCustomThumbnail && (
					<input
						accept=".jpg, .jpeg, .png"
						className="d-none"
						id="thumbnailFile"
						name="thumbnailFile"
						type="file"
						onChange={handleChangeImageFile}
						onError={handleChangeImageFileError}
					/>
				)}
			</div>
		);
	};

	const getSocialParagraph = () => {
		return (
			<div className="d-flex align-items-center mb-1">
				<div className="edit-thumbnail-text me-3">
					{t('editThumbnailNetworks')}
				</div>
				<div className="edit-thumbnail-social-icon position-relative d-flex align-items-center justify-content-center p-1 me-3">
					<span style={{ top: -1, left: -1, position: 'relative' }}>
						<Facebook />
					</span>
				</div>
				<div className="edit-thumbnail-social-icon d-flex align-items-center justify-content-center p-1 me-3">
					<Instagram />
				</div>
				<div className="edit-thumbnail-social-icon d-flex align-items-center justify-content-center p-1 me-3">
					<Linkedin />
				</div>
				<div className="edit-thumbnail-social-icon position-relative d-flex align-items-center justify-content-center p-1 me-3">
					<span style={{ top: 0, left: -1, position: 'relative' }}>
						<Youtube />
					</span>
				</div>
			</div>
		);
	};

	const renderUploader = () => {
		if (isLoading) {
			return <BayWidgetLoadingPlaceholder />;
		}

		if (!hasCustomThumbnail && !tempImage) {
			return (
				<div className="d-flex flex-column px-3 pt-0 pb-4">
					<div className="mb-3">{getSocialParagraph()}</div>
					<BayFilesUpload
						allowedFileTypes={{
							'image/jpeg': ['.jpg', '.jpeg'],
							'image/png': ['.png'],
						}}
						allowedFileTypeslabel=".JPG, .JPEG, .PNG"
						allowedFileTypesText="Maximum image file size - 5MB"
						isMultiple={false}
						noData={true}
						t={t}
						uploadingPopUpTitle={t('customThumbnail')}
						uploadLinkText={t('chooseFile')}
						onDrop={handleDrop}
					/>
				</div>
			);
		}

		return (
			<div className="d-flex flex-column px-4 pb-5">
				{getSocialParagraph()}
				{(hasCustomThumbnail || tempImage) && (
					<p>
						{t('customThumbnailAspectRatioMessage')}{' '}
						<span className="fw-bold">{t('customAspectRatioBoldMessage')}</span>
					</p>
				)}
				<div className="position-relative mt-4">
					<AvatarEditor
						ref={editorRef}
						border={20}
						color={[0, 0, 0, 0.6]}
						crossOrigin="anonymous"
						disableBoundaryChecks={false}
						height={375}
						image={tempImage || image || file?.thumbnailUrls?.default || ''}
						rotate={0}
						scale={scale}
						showGrid={showGrid}
						style={{ marginBottom: 20, borderRadius: 10 }}
						width={675}
					/>
				</div>
				<p>{t('editThumbnailZoomDescription')}</p>
				<div className="d-flex align-items-center justify-content-between w-100 mb-3">
					<BayRangeSlider
						className="flex-grow-1"
						max={3}
						min={1}
						step={0.01}
						value={scale}
						onChange={(e) => {
							const newScale = parseFloat(e.target.value);
							setScale(newScale);
							if (newScale !== initialScale) {
								setHasChanges(true);
							}
						}}
					/>
				</div>
			</div>
		);
	};

	return (
		<>
			<BayModal
				centered
				backdrop="static"
				footer={renderModalFooter()}
				height={680}
				noPadding={true}
				show={showModal.show}
				size="lg"
				title={t('customThumbnail')}
			>
				<Container>{renderUploader()}</Container>
			</BayModal>

			{showChangeImageFileError && (
				<BayModal
					buttonText={t('close')}
					hideCancel={true}
					show={showChangeImageFileError}
					submitFunc={() => setShowChangeImageFileError(false)}
					title={t('changeImageFileErrorTitle') || 'Error'}
				>
					<Container>
						<p>{changeImageFileErrorMsg}</p>
					</Container>
				</BayModal>
			)}
		</>
	);
};

export default EditThumbnailModal;
